import styled, { css } from 'styled-components';
import { layout, variant, space, color, border, flexbox } from 'styled-system';
import ClickedWrongX from '../../assets/ClickedWrongX.svg';
import ClickedRightCheck from '../../assets/ClickedRightCheck.svg';
import { shakeX } from '../../lib/ui/animations';

export const Button = styled.button`
  font-weight: 600;
  font-size: 14px;
  padding: ${props => (props.padding ? props.padding : '10px 24px')};
  border-radius: 8px;
  line-height: 18px;
  font-family: 'Montserrat', sans-serif;
  box-shadow: none;
  margin: 0;
  &.shakeX {
    animation: ${shakeX} 0.8s linear 1;
  }
  @media (prefers-reduced-motion) {
    &.shakeX {
      animation: none;
    }
  }
  ${color};
  ${border};
  ${space};
  ${layout};
  ${flexbox};

  &:hover,
  &:focus {
    ${({ borderColor, color, bg }) => css`
      border-color: ${borderColor};
      color: ${color};
      background: ${bg};
    `}
  }

  ${variant({
    variants: {
      containedBlue: {
        backgroundColor: 'primary.100',
        color: 'white',
        border: '1px solid',
        borderColor: 'primary.100',
        '&:hover': {
          borderColor: 'primary.80',
          backgroundColor: 'primary.80',
        },
        '&:focus': {
          backgroundColor: 'primary.120',
          borderColor: 'primary.120',
        },
        '&:disabled': {
          backgroundColor: 'primary.100',
          color: 'white',
          border: '1px solid',
          borderColor: 'primary.100',
          opacity: 0.2,
          pointerEvents: 'auto',
        },
      },
      outlinedBlue: {
        backgroundColor: 'white',
        color: 'primary.100',
        border: '1px solid',
        borderColor: 'primary.100',
        '&:hover': {
          color: 'primary.80',
          borderColor: 'primary.80',
          backgroundColor: 'white',
          svg: {
            path: {
              stroke: 'primary.80',
            },
          },
        },
        '&:focus': {
          color: 'primary.120',
          borderColor: 'primary.120',
          backgroundColor: 'white',
          svg: {
            path: {
              stroke: 'primary.120',
            },
          },
        },
        '&:disabled': {
          color: 'primary.100',
          borderColor: 'primary.100',
          backgroundColor: 'white',
          opacity: 0.2,
          pointerEvents: 'none',
        },
      },
      tertiary: {
        backgroundColor: 'white',
        color: 'black.80',
        border: '1px solid',
        borderColor: 'black.80',
        '&:hover': {
          color: 'black.60',
          borderColor: 'black.60',
          backgroundColor: 'white',
        },
        '&:focus': {
          color: 'black.100',
          borderColor: 'black.100',
          backgroundColor: 'white',
        },
        '&:disabled': {
          color: 'black.60',
          borderColor: 'black.60',
          backgroundColor: 'white',
          opacity: 0.2,
          pointerEvents: 'none',
        },
      },
      containedRed: {
        backgroundColor: 'notification',
        color: 'white',
        border: '1px solid',
        borderColor: 'notification',
        boxShadow: 'none',
        '&:hover': {
          backgroundColor: 'error.light',
        },
        '&:focus': {
          backgroundColor: 'error.darker',
          borderColor: 'error.darker',
        },
        '&:disabled': {
          backgroundColor: 'notification',
          color: 'white',
          border: '1px solid',
          borderColor: 'notification',
          boxShadow: 'none',
          opacity: 0.2,
          pointerEvents: 'none',
        },
        '&:disabled:hover': {
          backgroundColor: 'notification',
        },
      },
      outlinedRed: {
        backgroundColor: 'white',
        color: 'error.default',
        border: '1px solid',
        borderColor: 'error.default',
        '&:hover': {
          color: 'error.light',
          borderColor: 'error.light',
          backgroundColor: 'white',
          svg: {
            path: {
              stroke: 'error.light',
            },
          },
        },
        '&:focus': {
          color: 'error.darker',
          borderColor: 'error.darker',
          backgroundColor: 'white',
          svg: {
            path: {
              stroke: 'error.darker',
            },
          },
        },
        '&:disabled': {
          color: 'error.default',
          borderColor: 'error.default',
          backgroundColor: 'white',
          opacity: 0.2,
          pointerEvents: 'none',
        },
      },
      containedGreen: {
        backgroundColor: 'success.default',
        color: 'white',
        border: '1px solid',
        borderColor: 'success.default',
        boxShadow: 'none',
        '&:hover': {
          backgroundColor: 'success.light',
          borderColor: 'success.light',
        },
        '&:focus': {
          backgroundColor: 'success.darker',
          borderColor: 'success.darker',
        },
        '&:disabled': {
          backgroundColor: 'success.default',
          color: 'white',
          border: '1px solid',
          borderColor: 'success.default',
          boxShadow: 'none',
          opacity: 0.2,
          pointerEvents: 'auto',
        },
        '&:disabled:hover': {
          backgroundColor: 'success.default',
        },
      },
      answerBlue: {
        fontWeight: '400',
        fontSize: '16px',
        backgroundColor: 'primary.80',
        transition: 'background-color 800ms linear',
        '&&&:disabled': {
          backgroundColor: 'primary.70',
          opacity: '1',
        },
      },
      successGreen: {
        fontWeight: '400',
        fontSize: '16px',
        backgroundColor: 'success.button',
        borderColor: 'success.button',
        transition: 'background-color 800ms linear',
        reducedMotion: {
          transition: 'background-color 1200ms linear',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          backgroundColor: `success.buttonDarken`,
          backgroundImage: `url(${ClickedRightCheck})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: '50%',
          backgroundSize: '18px 13px',
          color: `white`,
          padding: '10px',
          width: ['15%', '10%'],
          height: '100%',
          top: '0',
          left: '0',
          borderRadius: '8px 0px 0px 8px',
        },
        '&&&:disabled': {
          backgroundColor: 'success.button',
          borderColor: 'success.button',
          opacity: '1',
        },
        '&&&:disabled:hover': {
          backgroundColor: 'success.button',
        },
        '&&&:focus': {
          backgroundColor: 'success.button',
          borderColor: 'success.button',
        },
      },
      errorRed: {
        fontWeight: '400',
        fontSize: '16px',
        backgroundColor: `error.button`,
        borderColor: 'error.button',
        transition: `background-color 800ms linear`,
        reducedMotion: {
          transition: 'background-color 1200ms linear',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          backgroundColor: `error.buttonDarken`,
          backgroundImage: `url(${ClickedWrongX})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: '50%',
          backgroundSize: '24px 24px',
          color: '#fff',
          padding: '10px',
          width: ['15%', '10%'],
          height: '100%',
          top: '0',
          left: '0',
          borderRadius: '8px 0px 0px 8px',
          transition: `background-color 800ms linear`,
        },
        '&&&:disabled': {
          backgroundColor: 'error.button',
          borderColor: 'error.button',
          opacity: '1',
        },
        '&&&:disabled:hover': {
          backgroundColor: 'error.button',
        },
        '&&&:focus': {
          backgroundColor: 'error.button',
          borderColor: 'error.button',
        },
      },
    },
  })}
`;

export const Anchor = styled.a`
  font-weight: 600;
  font-size: 14px;
  padding: 10px 24px;
  border-radius: 8px;
  line-height: 18px;
  font-family: 'Montserrat', sans-serif;
  ${space};
  ${variant({
    variants: {
      outlinedBlue: {
        backgroundColor: 'white',
        color: 'primary.100',
        border: '1px solid',
        borderColor: 'primary.100',

        '&:hover': {
          color: 'primary.80',
          borderColor: 'primary.80',
          backgroundColor: 'white',
        },
        '&:focus': {
          color: 'primary.120',
          borderColor: 'primary.120',
          backgroundColor: 'white',
        },
        '&:disabled': {
          color: 'primary.100',
          borderColor: 'primary.100',
          backgroundColor: 'white',
          opacity: 0.2,
          pointerEvents: 'none',
        },
      },
      tertiary: {
        backgroundColor: 'white',
        color: 'black.80',
        border: '1px solid',
        borderColor: 'black.80',
        '&:hover': {
          color: 'black.60',
          borderColor: 'black.60',
          backgroundColor: 'white',
        },
        '&:focus': {
          color: 'black.100',
          borderColor: 'black.100',
          backgroundColor: 'white',
        },
        '&:disabled': {
          color: 'black.60',
          borderColor: 'black.60',
          backgroundColor: 'white',
          opacity: 0.2,
          pointerEvents: 'none',
        },
      },
      containedBlue: {
        backgroundColor: 'primary.100',
        color: 'white',
        border: '1px solid',
        borderColor: 'primary.100',
        '&:hover': {
          borderColor: 'primary.80',
          backgroundColor: 'primary.80',
        },
        '&:focus': {
          backgroundColor: 'primary.120',
          borderColor: 'primary.120',
        },
        '&:disabled': {
          backgroundColor: 'primary.100',
          color: 'white',
          border: '1px solid',
          borderColor: 'primary.100',
          opacity: 0.2,
          pointerEvents: 'none',
        },
      },
      blueText: {
        color: 'primary.100',
        fontWeight: 500,
        padding: 0,

        '&:hover': {
          color: 'primary.80',
        },
      },
    },
  })},
`;
